// Use polyfills for modern language features
// The imports and dependencies can be removed if only modern browsers should be
// supported
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import 'core-js/features/array/find';
import 'core-js/features/array/includes';
import 'custom-event-polyfill';
import { AuthServiceProvider, AuthGate } from '@trp-ta-nitro/auth-react';
import { Constants, ModelManager } from '@adobe/cq-spa-page-model-manager';
import React from 'react';
import { render } from 'react-dom';
import { ConnectedRouter } from 'connected-react-router';
import App from './App';
import AppConfigLoader from './AppConfigLoader';
import { Provider } from 'react-redux';
import store, { history } from './redux/configure-store';
import './components/import-components';
import './index.css';
import PageModelLocal from 'page-model.local';

//  Get root element in dom where application will be rendered
const rootElement = document.getElementById('spa-root');

const isLocalhost = window && window.location.hostname.toLowerCase().indexOf('localhost.troweprice.com') !== -1;
let isAuthorEnvironment = false;
const isAuthorEnvironmentLocal = isLocalhost && window && window.location.port !== `${process.env.REACT_APP_PORT}`;
const configs = isLocalhost
  ? [
      `http://localhost.troweprice.com:${process.env.REACT_APP_PORT}/baseConfig.json`,
      `http://localhost.troweprice.com:${process.env.REACT_APP_PORT}/envConfig.json`,
    ]
  : ['baseConfig.json', 'envConfig.json'];

const AuthenticatedApp = (props: any) => {
  const { pageModel } = props;
  const reduxState = store.getState() as any;
  const apiKey = reduxState && reduxState.appConfig && reduxState.appConfig.apiKey;

  if (!apiKey) {
    console.log('apiKey is unavailable. AuthenticatedApp likely loaded before reduxStore.appConfig was initialized.');
    throw new Error(
      'apiKey is unavailable. AuthenticatedApp likely loaded before reduxStore.appConfig was initialized.',
    );
  }

  const authOptions = {
    apiKey,
    types: {
      // enable external and internal both, no extra options are required so none are passed
      external: {},
      internal: {},
    },
  };

  return (
    <AuthServiceProvider options={authOptions}>
      <AuthGate>
        <App
          history={history}
          cqChildren={pageModel[Constants.CHILDREN_PROP]}
          cqItems={pageModel[Constants.ITEMS_PROP]}
          cqItemsOrder={pageModel[Constants.ITEMS_ORDER_PROP]}
          cqPath={pageModel[Constants.PATH_PROP]}
          locationPathname={window.location.pathname}
        />
      </AuthGate>
    </AuthServiceProvider>
  );
};

const ConfiguredAndAuthenticatedApp = (props: any) => {
  const { pageModel } = props;
  return (
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <AppConfigLoader configs={configs}>
          <AuthenticatedApp pageModel={pageModel} />
        </AppConfigLoader>
      </ConnectedRouter>
    </Provider>
  );
};

const RootComponent = (props: any) => {
  const { pageModel } = props;
  return (
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <App
          history={history}
          cqChildren={pageModel[Constants.CHILDREN_PROP]}
          cqItems={pageModel[Constants.ITEMS_PROP]}
          cqItemsOrder={pageModel[Constants.ITEMS_ORDER_PROP]}
          cqPath={pageModel[Constants.PATH_PROP]}
          locationPathname={window.location.pathname}
        />
      </ConnectedRouter>
    </Provider>
  );
};

const isEmptyObject = (obj: any) => {
  for (const property in obj) {
    return false;
  }
  return true;
};

const checkAndResetPageModel = (pageModel: any) => {
  if (
    !pageModel ||
    !pageModel[':items'] ||
    !pageModel[':items'].root ||
    !pageModel[':items'].root[':items'] ||
    !pageModel[':items'].root[':items'].responsivegrid ||
    !pageModel[':items'].root[':items'].responsivegrid[':items']
  ) {
    return pageModel;
  }

  if (pageModel[':items'].root[':items'].responsivegrid[':items'].retirementplannercon) {
    pageModel[':items'].root[':items'].responsivegrid[':items'].retirementplannercon =
      PageModelLocal.retirementplannercon;
  }

  if (pageModel[':items'].root[':items'].responsivegrid[':items'].strategycontainer) {
    pageModel[':items'].root[':items'].responsivegrid[':items'].strategycontainer = PageModelLocal.strategycontainer;
  }

  if (pageModel[':items'].root[':items'].responsivegrid[':items'].educationcontainer) {
    pageModel[':items'].root[':items'].responsivegrid[':items'].educationcontainer = PageModelLocal.educationcontainer;
  }

  if (pageModel[':items'].root[':items'].responsivegrid[':items'].xfscripts) {
    pageModel[':items'].root[':items'].responsivegrid[':items'].xfscripts = PageModelLocal.xfscripts;
  }

  return pageModel;
};

document.addEventListener('DOMContentLoaded', () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetch('/content/workplace/en/participant/ri/index.model.json').then(response => {
    if (response.headers.get('X-Vhost') === 'author') {
      isAuthorEnvironment = true;
    }
    return;
  });

  ModelManager.initialize({ path: '/content/workplace/en/participant/ri/index' }).then((pageModel: any) => {
    pageModel = checkAndResetPageModel(pageModel);

    if (isAuthorEnvironmentLocal || isAuthorEnvironment) {
      render(<RootComponent pageModel={pageModel} />, rootElement);
    } else {
      render(<ConfiguredAndAuthenticatedApp pageModel={pageModel} />, rootElement);
    }
  });
});
